import React from 'react';
import { Typography, Card } from 'antd';
import AverageTagSmall from '../components/averageTagSmall/averageTagSmall';
import AutoSizeTitle from '../components/autoSizeTitle/AutoSizeTitle';
import LeadershipByOrgItems from '../components/leadershipByOrgItems/leadershipByOrgItems';

const { Title, Text } = Typography;

/*
Props:
- selectedEvaluation
- average: promedio global de cultura (o de la categoría si aplica)
- item: { title, subtitle, singularSubtitle }
- pageNumber
- chartData: [{ name, average }]
- isFirstPage
- summaryMode: 'category-dimensions' | 'org-items' | 'subdimensions'
- categoryName: string (para mensajes de categoría)
- entityLabel: string (para org-items: 'La gerencia', 'El cargo', etc)
*/
const PageCultureResultsByItems = ({
  selectedEvaluation,
  average,
  item,
  pageNumber,
  chartData = [],
  isFirstPage = false,
  summaryMode = 'org-items',
  categoryName = '',
  entityLabel = ''
}) => {
  const backgroundStyle = {
    backgroundImage: `url('/informs/pagina.png')`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'left top',
    backgroundSize: '100% auto',
  };

  const getColor = (value) => {
    if (value <= selectedEvaluation.evl_max_red_light) return "#e53935";
    if (value <= selectedEvaluation.evl_max_yellow_light) return "#fbc02d";
    return "#43a047";
  };

  const buildCustomSummary = () => {
    if (!chartData || chartData.length === 0) return null;
    const sorted = [...chartData].sort((a, b) => b.average - a.average);
    const best = sorted[0];
    const worst = sorted[sorted.length - 1];

    if (summaryMode === 'category-dimensions') {
      // Frase por categoría: Atributo cultural/Palanca/Ítem de compromiso
      const catLower = (categoryName || '').toLowerCase();
      let noun = 'El atributo cultural';
      if (catLower.includes('palanca')) noun = 'La palanca';
      else if (catLower.includes('compromiso')) noun = 'El ítem de compromiso';

      return (
        <Text style={{ fontSize: '12px', textAlign: 'justify' }}>
          {noun} más fuerte que tiene la organización es <Text strong>{best.name} ({best.average.toFixed(1)}%)</Text>.{' '}
          El {noun.toLowerCase()} que más falta fortalecer es <Text strong>{worst.name} ({worst.average.toFixed(1)}%)</Text>.
        </Text>
      );
    }

    if (summaryMode === 'org-items') {
      const label = entityLabel || 'La entidad';
      return (
        <Text style={{ fontSize: '12px', textAlign: 'justify' }}>
          {label} que obtuvo mejor resultado fue <Text strong>{best.name} ({best.average.toFixed(1)}%)</Text>.{' '}
          {label} con el resultado más bajo fue <Text strong>{worst.name} ({worst.average.toFixed(1)}%)</Text>.
        </Text>
      );
    }

    if (summaryMode === 'subdimensions') {
      return (
        <Text style={{ fontSize: '12px', textAlign: 'justify' }}>
          La pregunta con mayor favorabilidad fue <Text strong>{best.name} ({best.average.toFixed(1)}%)</Text>.{' '}
          La pregunta con menor favorabilidad fue <Text strong>{worst.name} ({worst.average.toFixed(1)}%)</Text>.
        </Text>
      );
    }

    return null;
  };

  const customSummary = buildCustomSummary();

  return (
    <div className="pdf-page internal-page">
      <div className="internal-page-content" style={backgroundStyle}>
        <AutoSizeTitle text={selectedEvaluation.evl_title} />

        <div className="internal-page-info" style={{ position: 'relative' }}>
          <AverageTagSmall average={average} color={getColor(average)} type={1} />

          <Card
            style={{
              borderRadius: '8px',
              border: '1px solid #d9d9d9',
              backgroundColor: '#fafafaff',
              boxShadow: 'none',
            }}
            styles={{ body: { padding: '16px 24px' } }}
          >
            <Title level={4} style={{ color: '#339C98', marginBottom: 16, width: 'calc(100% - 150px)' }}>
              {item.title}
            </Title>

            <LeadershipByOrgItems
              subtitle={item.subtitle}
              singularSubtitle={item.singularSubtitle}
              chartData={chartData}
              redLight={selectedEvaluation.evl_max_red_light}
              yellowLight={selectedEvaluation.evl_max_yellow_light}
              isFirstPage={isFirstPage}
              customSummary={customSummary}
            />
          </Card>
        </div>

        <div className="internal-page-number">{pageNumber}</div>
      </div>
    </div>
  );
};

export default PageCultureResultsByItems;
